import { useState } from 'react';
import { CheckCircle, Download, RefreshCw, Stethoscope } from '../shared/icons';

const VALID_PASSWORDS = ['adi2305', 'san1606', 'vncpl2924'];

const ResultStep = ({ score, testType, patientDetails, analysis, config, onGeneratePDF, onNewAssessment }) => {
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleDownloadClick = () => {
    setShowPasswordPrompt(true);
    setPassword('');
    setPasswordError('');
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (VALID_PASSWORDS.includes(password.trim())) {
      setShowPasswordPrompt(false);
      setPassword('');
      setPasswordError('');
      onGeneratePDF();
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleCancelPassword = () => {
    setShowPasswordPrompt(false);
    setPassword('');
    setPasswordError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-lg lg:max-w-xl animate-slide-up relative z-10">
        <div className="glass-panel rounded-3xl overflow-hidden shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-8 sm:p-12 text-center relative">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
              <div className="text-blue-800">
                <CheckCircle size={48} strokeWidth={2.5} />
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-navy-dark mb-4">
              Thank You, {patientDetails.patient}
            </h1>
            <p className="text-brand-neutral-600 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
              Your assessment has been completed successfully.
            </p>
          </div>

          {/* Content section */}
          <div className="p-6 sm:p-8 lg:p-10">
            {/* Next steps message */}
            <div className="bg-brand-neutral-50 border border-brand-neutral-200 rounded-2xl p-6 sm:p-8 mb-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Stethoscope size={20} className="text-blue-800" />
                </div>
                <div>
                  <h3 className="text-brand-navy-dark font-bold mb-2 text-base sm:text-lg">
                    Next Steps
                  </h3>
                  <p className="text-brand-neutral-700 leading-relaxed text-sm sm:text-base">
                    Please contact <span className="font-semibold text-brand-navy-dark">{patientDetails.doctor}</span> to discuss your results and determine the appropriate next steps for your care.
                  </p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            {!showPasswordPrompt ? (
              <div className="space-y-3">
                <button
                  onClick={handleDownloadClick}
                  className="w-full py-4 sm:py-5 bg-blue-800 text-white rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-2xl flex items-center justify-center gap-2 hover:-translate-y-1 active:translate-y-0 relative overflow-hidden group ring-2 ring-blue-400/30 hover:ring-blue-500/50"
                >
                  <div className="absolute inset-0 bg-blue-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Download size={20} className="relative z-10" />
                  <span className="relative z-10 font-bold tracking-wide">Download Clinical Report</span>
                </button>
                <button
                  onClick={onNewAssessment}
                  className="w-full py-4 sm:py-5 bg-white border-2 border-brand-neutral-200 hover:border-blue-500 hover:bg-blue-50 hover:shadow-md text-brand-neutral-700 hover:text-blue-800 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 hover:-translate-y-0.5 active:translate-y-0"
                >
                  <RefreshCw size={18} />
                  <span className="font-bold">New Assessment</span>
                </button>
              </div>
            ) : (
              <div className="bg-white border-2 border-blue-200 rounded-2xl p-6 sm:p-8 animate-fade-in">
                <h3 className="text-brand-navy-dark font-bold mb-2 text-base sm:text-lg text-center">
                  Professional Access Required
                </h3>
                <p className="text-brand-neutral-600 text-sm mb-6 text-center">
                  Please enter your authorized password to download the report.
                </p>
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      autoFocus
                      className="w-full px-4 py-3 rounded-xl border-2 border-brand-neutral-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-center font-mono text-lg tracking-wider"
                    />
                    {passwordError && (
                      <p className="text-red-600 text-sm mt-2 text-center font-medium">
                        {passwordError}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleCancelPassword}
                      className="flex-1 py-3 bg-white border-2 border-brand-neutral-200 hover:bg-brand-neutral-50 text-brand-neutral-700 rounded-xl font-semibold transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 py-3 bg-blue-800 hover:bg-blue-900 text-white rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 animate-fade-in-slow">
          <p className="text-brand-neutral-500 text-xs sm:text-sm font-medium">
            © 2026 Vohra Neuropsychiatry Centre
          </p>
          <p className="text-brand-neutral-400 text-xs mt-1">
            All assessments are confidential and HIPAA compliant
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResultStep;
